// Item art: a line drawing of the kind of thing, on a tile tinted for that kind.
// No photos and no people. The same kind always gets the same drawing and tint, so the feed reads as one set.

import { raw } from './html.ts';
import type { Trusted } from './html.ts';

interface Kind {
  words: string[];
  hue: number;
  draw: string;
}

const KINDS: Record<string, Kind> = {
  cycle: {
    words: ['cycle', 'bicycle', 'bike', 'साइकिल'],
    hue: 222,
    draw: '<circle cx="18" cy="40" r="10"/><circle cx="46" cy="40" r="10"/><path d="M18 40h12l12-16H27l3 16M18 40l9-18M42 24l4 16M24 21h6M42 24l-2-5h5"/>',
  },
  cooker: {
    words: ['cooker', 'kadai', 'pan', 'kitchen', 'कुकर'],
    hue: 48,
    draw: '<path d="M16 30h32v13a7 7 0 01-7 7H23a7 7 0 01-7-7z"/><path d="M13 30h38M48 27h10M32 30v-6M28.5 23.5h7"/>',
  },
  fan: {
    words: ['fan', 'पंखा'],
    hue: 188,
    draw: '<path d="M32 9v17"/><circle cx="32" cy="30" r="4"/><ellipse cx="17" cy="31" rx="11" ry="3.6"/><ellipse cx="17" cy="31" rx="11" ry="3.6" transform="rotate(120 32 30)"/><ellipse cx="17" cy="31" rx="11" ry="3.6" transform="rotate(240 32 30)"/>',
  },
  books: {
    words: ['book', 'books', 'ncert', 'novel', 'किताब'],
    hue: 22,
    draw: '<rect x="13" y="40" width="38" height="9" rx="1.5"/><rect x="17" y="31" width="32" height="9" rx="1.5"/><rect x="15" y="22" width="30" height="9" rx="1.5"/><path d="M19 44.5h10M22 35.5h8M20 26.5h7"/>',
  },
  sewing: {
    words: ['sewing', 'singer', 'सिलाई'],
    hue: 340,
    draw: '<path d="M12 48h40"/><path d="M18 48V24a4 4 0 014-4h22a4 4 0 014 4v9H30v15"/><path d="M42 33v7M40 40h4M24 20v-5M21.5 15h5"/><circle cx="41" cy="25" r="2"/>',
  },
  plant: {
    words: ['plant', 'sapling', 'saplings', 'tulsi', 'pot', 'पौधा', 'पौधे'],
    hue: 142,
    draw: '<path d="M21 40h22l-3 12H24z"/><path d="M32 40V24"/><path d="M32 32c-8 0-12-5-12-10 8 0 12 4 12 10zM32 26c8 0 12-5 12-10-8 0-12 4-12 10z"/>',
  },
  phone: {
    words: ['phone', 'iphone', 'mobile', 'redmi', 'samsung', 'फ़ोन', 'फोन'],
    hue: 262,
    draw: '<rect x="21" y="9" width="22" height="46" rx="5"/><path d="M28.5 13.5h7M29 50h6"/>',
  },
  kit: {
    words: ['kit', 'drawing', 'drafter', 'geometry', 'instrument'],
    hue: 84,
    draw: '<path d="M13 49V19l30 30z"/><path d="M19 43v-9l9 9z"/><path d="M48 13l-6 26M48 13l6 26M48 13v-3"/>',
  },
  cooler: {
    words: ['cooler', 'ac', 'air', 'कूलर'],
    hue: 200,
    draw: '<rect x="18" y="10" width="28" height="40" rx="3"/><path d="M23 18h18M23 23h18M23 28h18M23 33h18M23 42h6M22 50v4M42 50v4"/>',
  },
  almirah: {
    words: ['almirah', 'cupboard', 'wardrobe', 'shelf', 'table', 'chair', 'bed', 'sofa', 'अलमारी'],
    hue: 58,
    draw: '<rect x="17" y="9" width="30" height="43" rx="2"/><path d="M32 9v43M29 28v5M35 28v5M20 52v3M44 52v3"/>',
  },
  other: {
    words: [],
    hue: 72,
    draw: '<path d="M12 33l19-19h17v17L29 50z"/><circle cx="40" cy="22" r="2.6"/>',
  },
};

// The kinds a seller can pick on the Sell form, in the order they are offered.
export const PICKABLE = ['almirah', 'cooker', 'phone', 'fan', 'books', 'cycle', 'plant', 'other'] as const;
export type Pickable = (typeof PICKABLE)[number];

export function kindArt(kind: string, size: 'tile' | 'hero' | 'thumb' = 'thumb'): Trusted {
  const k = KINDS[kind] ?? KINDS.other!;
  return raw(`<span class="art art-${size}" style="--hue:${k.hue}" aria-hidden="true"><svg viewBox="0 0 64 64" focusable="false">${k.draw}</svg></span>`);
}

function kindOf(title: string, picked?: string): Kind {
  if (picked && picked !== 'other' && KINDS[picked]) return KINDS[picked]!;
  const words = title.toLowerCase().split(/[^a-z0-9ऀ-ॿ]+/);
  for (const k of Object.values(KINDS)) if (k.words.some((x) => words.includes(x))) return k;
  return KINDS.other!;
}

// The seller's own pick wins; a title is read for a kind only when nothing was picked.
export function itemArt(title: string, size: 'tile' | 'hero' | 'thumb' = 'tile', picked?: string): Trusted {
  const k = kindOf(title, picked);
  return raw(
    `<span class="art art-${size}" style="--hue:${k.hue}" aria-hidden="true"><svg viewBox="0 0 64 64" focusable="false">${k.draw}</svg></span>`,
  );
}
